/**
 * A2UI (Agent-to-User Interface) — spec-subset types + payload parsing.
 *
 * A2UI is Google's open protocol for agent-driven UI (Apache 2.0,
 * https://a2ui.org, spec v0.9.1): the agent emits DECLARATIVE JSON —
 * a flat component adjacency list + a separate data model — and the
 * client renders it with its OWN component catalog. UI is data, never
 * code: the model can only reference components we ship, so this is the
 * same trust posture as the mini-app catalog philosophy.
 *
 * We implement a deliberate SUBSET, spec-shaped so payloads stay
 * portable (docs/plans/2026-07-04_CHAT_UI_REDESIGN.md §4):
 *   • components: flat list, `id` + `component` + props; tree via
 *     `child` / `children` id references (adjacency list)
 *   • data binding: any prop value of shape `{ "path": "/a/b" }` reads
 *     from the data model; input components WRITE to their bound path
 *   • events: `action: { event: { name, context? } }` on interactive
 *     components; firing surfaces `{ name, context, data: <model> }`
 *
 * Streamed createSurface/updateComponents/updateDataModel messages are
 * NOT implemented — a persisted chat part carries the RESOLVED surface
 * (this shape). The agent-side tool builds it in one shot.
 */

/** A data-model reference — any prop value of this shape is a binding. */
export interface A2UIBinding {
  path: string
}

export interface A2UIAction {
  event: {
    name: string
    /** Optional static payload echoed back with the event. */
    context?: Record<string, unknown>
  }
}

export interface A2UIComponent {
  id: string
  /** Catalog name — unknown names render a fail-soft placeholder. */
  component: string
  /** Single-child reference (spec shape, e.g. Button → its label Text). */
  child?: string
  /** Multi-children references (Row / Column / Card). */
  children?: string[]
  action?: A2UIAction
  /** Component-specific props; values may be literals or bindings. */
  [prop: string]: unknown
}

export interface A2UISurfaceDef {
  version?: string
  surfaceId?: string
  /** id of the root component in `components`. */
  root: string
  components: A2UIComponent[]
  /** Initial data model the bindings read from / write to. */
  dataModel?: Record<string, unknown>
}

/** What A2UISurface hands back when the user fires an event. */
export interface A2UIEvent {
  name: string
  context?: Record<string, unknown>
  /** Snapshot of the data model at fire time (client→agent data sync). */
  data: Record<string, unknown>
}

export function isBinding(v: unknown): v is A2UIBinding {
  return !!v && typeof v === 'object' && !Array.isArray(v)
    && typeof (v as A2UIBinding).path === 'string'
    && Object.keys(v as object).length === 1
}

/* ─── data model paths ("/a/b/0", JSON-pointer-lite) ─────────────────── */

function segments(path: string): string[] {
  return path.split('/').filter(Boolean)
}

export function getPath(model: unknown, path: string): unknown {
  let cur: unknown = model
  for (const seg of segments(path)) {
    if (cur == null || typeof cur !== 'object') return undefined
    cur = Array.isArray(cur) ? cur[Number(seg)] : (cur as Record<string, unknown>)[seg]
  }
  return cur
}

/** Immutable set — returns a new model with `path` set to `value`.
 *  Intermediate objects are created as needed. */
export function setPath(
  model: Record<string, unknown>,
  path: string,
  value: unknown,
): Record<string, unknown> {
  const segs = segments(path)
  if (segs.length === 0) return model
  const next = { ...model }
  let cur: Record<string, unknown> = next
  for (let i = 0; i < segs.length - 1; i++) {
    const seg = segs[i]
    const existing = cur[seg]
    const copy: Record<string, unknown> =
      existing && typeof existing === 'object' && !Array.isArray(existing)
        ? { ...(existing as Record<string, unknown>) }
        : {}
    cur[seg] = copy
    cur = copy
  }
  cur[segs[segs.length - 1]] = value
  return next
}

/* ─── payload parsing ────────────────────────────────────────────────── */

/**
 * Structurally validate an untrusted payload (LLM output / tool metadata)
 * into an A2UISurfaceDef. Returns null when the shape is unusable —
 * callers fail soft (render nothing / fall back to raw JSON), never throw.
 */
export function parseA2UISurface(raw: unknown): A2UISurfaceDef | null {
  if (!raw || typeof raw !== 'object') return null
  const o = raw as Record<string, unknown>
  if (typeof o.root !== 'string' || !Array.isArray(o.components)) return null

  const components: A2UIComponent[] = []
  for (const c of o.components) {
    if (!c || typeof c !== 'object') continue
    const cc = c as Record<string, unknown>
    if (typeof cc.id !== 'string' || typeof cc.component !== 'string') continue
    if (cc.child !== undefined && typeof cc.child !== 'string') continue
    if (cc.children !== undefined
      && !(Array.isArray(cc.children) && cc.children.every(x => typeof x === 'string'))) continue
    components.push(cc as unknown as A2UIComponent)
  }
  if (components.length === 0) return null
  if (!components.some(c => c.id === o.root)) return null

  const dataModel = (o.dataModel && typeof o.dataModel === 'object' && !Array.isArray(o.dataModel))
    ? o.dataModel as Record<string, unknown>
    : undefined

  return {
    version: typeof o.version === 'string' ? o.version : undefined,
    surfaceId: typeof o.surfaceId === 'string' ? o.surfaceId : undefined,
    root: o.root,
    components,
    dataModel,
  }
}
